import React, { useState, useEffect, useContext } from "react";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { Plus, Trash2, BellPlus } from "lucide-react";
import { translateCategory } from "../utils/categoryTranslations";
import { toLatin } from "../utils/textDictionary";

//Remove default icon loaders
delete L.Icon.Default.prototype._getIconUrl;
//Add custom marker icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: purpleMarker,
  iconUrl: purpleMarker,
  shadowUrl: markerShadow,
});

//main component
const CustomizeNotifications = () => {
  const { user, accessToken, language } = useContext(AuthContext);
  const navigate = useNavigate();

  //Backend data
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  //User choices
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedAreasList, setSelectedAreasList] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedArea, setSelectedArea] = useState("");

  //Object for categories and stores
  const [selected, setSelected] = useState({
    categories: [],
    stores: [],
  });

  const translations = {
    el: {
      title: "Προσαρμογή ειδοποιήσεων",
      chooseCategory: "Επιλογή κατηγορίας",
      selectCategory: "Επιλέξτε κατηγορία",
      chooseCity: "Επιλογή πόλης",
      selectCity: "Επιλέξτε πόλη",
      chooseArea: "Επιλογή περιοχής",
      selectArea: "Επιλέξτε περιοχή",
      chooseStore: "Επιλογή καταστήματος",
      selectStore: "Επιλέξτε κατάστημα",
      removeFromPrefs: "Αφαίρεση από τις προτιμήσεις",
      savePrefs: "Αποθήκευση προτιμήσεων",
      prefsSaved: "Οι προτιμήσεις αποθηκεύτηκαν!",
      prefsSuccessMsg: "Οι ρυθμίσεις των ειδοποιήσεών σας αποθηκεύτηκαν με επιτυχία.",
      ok: "Εντάξει",
      searching: "Αναζήτηση...",
      selectedPoint: "Επιλεγμένο σημείο",
      pointOnMap: "Σημείο στο χάρτη",
      customLocation: "Προσαρμοσμένη τοποθεσία",
      atLeastOne: "Παρακαλώ επιλέξτε τουλάχιστον μία προτίμηση."
    },
    en: {
      title: "Customize your notifications",
      chooseCategory: "Choose category",
      selectCategory: "Select a category",
      chooseCity: "Choose city",
      selectCity: "Select city",
      chooseArea: "Choose area",
      selectArea: "Select area",
      chooseStore: "Choose store",
      selectStore: "Select store",
      removeFromPrefs: "Remove from preferences",
      savePrefs: "Save preferences",
      prefsSaved: "Preferences saved!",
      prefsSuccessMsg: "Your notification preferences have been successfully saved.",
      ok: "OK",
      searching: "Searching...",
      selectedPoint: "Selected point",
      pointOnMap: "Point on map",
      customLocation: "Custom location",
      atLeastOne: "Please select at least one preference."
    }
  };

  const t = translations[language] || translations.el;

  //map selected areas
  const [selectedAreas, setSelectedAreas] = useState([]);
  //default location on map (Athens)
  const [defaultCenter, setDefaultCenter] = useState({ lat: 37.9838, lng: 23.7275 });
  //popup confirmational modal
  const [showSavePopup, setShowSavePopup] = useState(false);

  //fetch initial data
  useEffect(() => {
    if (!user || !accessToken) return;

    const fetchInitial = async () => {
      setSelectedCity("");
      setSelectedArea("");
      setSelectedAreasList([]);

      try {
        const [catRes, cityRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/preferences/categories?lang=${language}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/preferences/cities?lang=${language}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        ]);

        //set states
        setCategories(await catRes.json());
        const cityData = await cityRes.json();
        setCities(Array.isArray(cityData) ? cityData : []);


        const prefsRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/preferences/userPrefs?lang=${language}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (prefsRes.ok) {
          const prefs = await prefsRes.json();

          // Categories & Stores (IDs)
          setSelected({
            categories: prefs.categories || [],
            stores: prefs.stores || [],
          });

          // Store full store objects for display in the list below the dropdown
          if (prefs.fullStores) setStores(prefs.fullStores);

          // Cities
          setSelectedCities(prefs.cities || []);
          if (prefs.cities?.length > 0) {
            setSelectedCity(prefs.cities[0]); // Τοποθετεί το αντικείμενο {el, en}
          }

          // Areas
          setSelectedAreasList(prefs.areasBySelect || []);

          // Map Markers
          setSelectedAreas(prefs.areas || []);
          if (prefs.areas?.length > 0) {
            setDefaultCenter({ lat: prefs.areas[0].lat, lng: prefs.areas[0].lng });
          }
        }
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    fetchInitial();
  }, [user, accessToken, language]);

  //fetch areas by the city selected in the dropdown
  useEffect(() => {
    if (!selectedCity) return;

    // Στέλνουμε πάντα το .el στο backend για να βρει τις περιοχές
    const citySearchTerm = typeof selectedCity === 'object' ? selectedCity.el : selectedCity;

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/preferences/areasByCity?city_el=${encodeURIComponent(citySearchTerm)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
      .then(res => res.json())
      .then(data => {
        setAreas(Array.isArray(data) ? data : []);
        setSelectedArea("");
      });
  }, [selectedCity, accessToken]);

  //fetch stores based on the city and area
  // Αντικατάστησε το υπάρχον useEffect για τα stores με αυτό:
  useEffect(() => {
    // Ελέγχουμε αν υπάρχει επιλεγμένη πόλη (ως object πλέον)
    if (!selectedCity || !selectedCity.el) {
      setStores([]);
      setSelected(prev => ({ ...prev, stores: [] })); // ΚΑΘΑΡΙΣΜΟΣ: Αφαιρεί τα επιλεγμένα IDs
      return;
    }

    const params = new URLSearchParams();
    // Στέλνουμε πάντα το .el στο backend για να ξέρει τι να ψάξει στη βάση
    params.append("city", selectedCity.el);
    params.append("lang", language);

    // Μετατρέπουμε τα area objects σε λίστα από strings (χρησιμοποιώντας το .el)
    if (selectedAreasList.length > 0) {
      selectedAreasList.forEach(areaObj => {
        // Σιγουρευόμαστε ότι παίρνουμε το string, είτε είναι object είτε string
        const areaValue = typeof areaObj === 'object' ? areaObj.el : areaObj;
        params.append("areas", areaValue);
      });
    }

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/preferences/storesByAreas?${params.toString()}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return setStores([]);
        setStores(data);
      })
      .catch(() => setStores([]));

  }, [selectedCity, selectedAreasList, accessToken, language]);

  //handles users choice of removal for category or store
  const handleRemove = (type, id) => {
    const listKey = type === "category" ? "categories" : "stores";
    setSelected((prev) => ({
      ...prev,
      //keeps all id's other than the one i want to filter out
      [listKey]: prev[listKey].filter((item) => item !== id),
    }));
  };

  //remove marker from the map
  const handleRemoveArea = (uidToRemove) => {
    setSelectedAreas(prev => prev.filter(area => area.uid !== uidToRemove));
  };

  const removeCity = (cityObj) => {
    // 1. Αφαιρούμε την πόλη από τη λίστα πόλεων
    const updatedCities = selectedCities.filter(c => c.el !== cityObj.el);
    setSelectedCities(updatedCities);

    // 2. Αφαιρούμε όλες τις περιοχές που ανήκουν σε αυτή την πόλη
    setSelectedAreasList(prev => prev.filter(area => area.city_el !== cityObj.el));

    // 3. Αφαιρούμε τα επιλεγμένα stores που ανήκουν σε αυτή την πόλη
    // Πρέπει να βρούμε ποια stores στην τρέχουσα λίστα 'stores' ανήκουν στην πόλη
    setSelected(prev => ({
      ...prev,
      stores: prev.stores.filter(storeId => {
        const storeData = stores.find(s => s.store_id === storeId);
        // Αν το store υπάρχει στη λίστα stores και η πόλη του είναι η διαγραμμένη, το αφαιρούμε
        // Αν δεν το βρούμε (π.χ. δεν έχει φορτωθεί), το κρατάμε ή το αφαιρούμε κατά βούληση
        return storeData ? storeData.city !== cityObj.el : true;
      })
    }));

    // 4. Αν η πόλη που διαγράφηκε ήταν αυτή που ήταν επιλεγμένη στο dropdown, καθάρισέ το
    const currentCityEl = typeof selectedCity === 'object' ? selectedCity.el : selectedCity;
    if (currentCityEl === cityObj.el) {
      setSelectedCity("");
      setAreas([]); // Καθαρίζουμε και τις διαθέσιμες περιοχές του dropdown
      setSelectedArea("");
    }
  };

  // Αλλαγή στη removeAreaFromList
  const removeAreaFromList = (areaObj) => {
    setSelectedAreasList(prev => prev.filter(a => a.en !== areaObj.en));
    if (selectedArea === areaObj.en || selectedArea === areaObj.el) setSelectedArea("");
  };

  //clears all city dependencies
  const resetCityDependencies = () => {
    setSelectedArea("");
    setSelectedAreasList([]);
    setStores([]);
    setSelected(prev => ({ ...prev, stores: [] }));
  };

  // Προσθήκη της handleSelect για κατηγορίες και καταστήματα
  const handleSelect = (type, value) => {
    if (!value) return;

    if (type === "category") {
      const categoryId = parseInt(value);
      if (!selected.categories.includes(categoryId)) {
        setSelected((prev) => ({
          ...prev,
          categories: [...prev.categories, categoryId],
        }));
      }
    } else if (type === "store") {
      const storeId = parseInt(value);
      if (!selected.stores.includes(storeId)) {
        setSelected((prev) => ({
          ...prev,
          stores: [...prev.stores, storeId],
        }));
      }
    }
  };

  //map components
  const LocationMarker = ({ language, t }) => {
    //everytime the user click on the map the function runs
    useMapEvents({
      async click(e) {
        const { lat, lng } = e.latlng;
        const newUid = crypto.randomUUID();

        // Προσθήκη προσωρινού marker
        setSelectedAreas(prev => [
          ...prev,
          { uid: newUid, lat, lng, location_el: "Αναζήτηση...", location_en: "Searching..." },
        ]);

        try {
          // Ζητάμε την διεύθυνση και στις δύο γλώσσες (κάνοντας 2 requests ή χρησιμοποιώντας το toLatin)
          // Εδώ για οικονομία θα χρησιμοποιήσουμε το toLatin που ήδη έχεις για την English εκδοχή
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=el`
          );
          const data = await res.json();

          if (data.address) {
            const addr = data.address;
            const road = addr.road || addr.pedestrian || addr.suburb || "";
            const city = addr.city || addr.town || addr.village || "";

            const cleanAddressEl = [road, city].filter(part => part.trim().length > 0).join(", ");
            const cleanAddressEn = toLatin(cleanAddressEl); // Χρήση του utility σου

            setSelectedAreas(prev =>
              prev.map(a => (a.uid === newUid ? {
                ...a,
                location_el: cleanAddressEl || "Σημείο στο χάρτη",
                location_en: cleanAddressEn || "Point on Map"
              } : a))
            );
          }
        } catch (err) {
          console.error("Nominatim error", err);
        }
      },
    });

    //for every item in the selectedAreas, create a marker on the map
    return (
      <>
        {selectedAreas.map((area) => (
          <Marker key={area.uid || area.preference_id} position={[area.lat, area.lng]} />
        ))}
      </>
    );
  };

  //creates a pin in the position returned
  const MapController = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      if (center) map.flyTo(center, map.getZoom());
    }, [center, map]);
    return null;
  };

  //Handle save
  const handleSavePreferences = async () => {
    if (
      !selected.categories.length &&
      !selected.stores.length &&
      !selectedAreas.length && // markers
      !selectedCities.length &&
      !selectedAreasList.length
    ) {
      alert(t.atLeastOne);
      return;
    }

    const payload = {
      category_ids: selected.categories,
      store_ids: selected.stores,
      cities: selectedCities, // Στέλνει [{el: 'Αθήνα', en: 'Athens'}]
      areasBySelect: selectedAreasList, // Στέλνει [{el: 'Χαλάνδρι', en: 'Chalandri', city_el: '...', ...}]
      areas: selectedAreas, // Τα markers του χάρτη (που έχουν location_el/en)
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/preferences/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) setShowSavePopup(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="prefs-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 pt-20 md:pt-10 xl:ml-20 text-center md:text-left">
          <h2 className="text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
            <BellPlus className="text-custom-grad1" /> {t.title}
          </h2>

          <div className="flex flex-col gap-5 w-full max-w-md mx-auto">

            {/* Category */}
            <div className="mt-4 flex flex-col gap-2">
              <label className="block w-full text-center font-semibold mb-2 text-custom-letters">{t.chooseCategory}</label>
              <select
                className="w-full p-2 rounded-lg border border-custom-letters text-custom-letters focus:ring-2 focus:ring-custom-grad1"
                onChange={(e) => handleSelect("category", e.target.value)}
                defaultValue=""
              >
                <option value="">{t.selectCategory}</option>
                {/*loops through the array to create all the options*/}
                {categories.map((cat) => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {language === "el" ? cat.category_name_el : cat.category_name}
                  </option>
                ))}

              </select>
              <div className="mt-2 flex flex-col gap-2">
                {selected.categories.map((id) => {
                  const category = categories.find((c) => c.category_id === id);
                  return (
                    <div key={id} className="flex items-center justify-between bg-custom-header rounded-full shadow p-3">
                      <span className="font-medium text-custom-letters">
                        {language === "el"
                          ? category?.category_name_el
                          : category?.category_name}
                      </span>
                      <button
                        title={t.removeFromPrefs}
                        onClick={() => handleRemove("category", id)} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-bold px-4 py-2 rounded-full">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* City*/}
            <div className="mt-4 flex flex-col gap-2">
              <label className="block w-full text-center font-semibold mb-2 text-custom-letters">{t.chooseCity}</label>
              <select
                className="w-full p-2 rounded-lg border"
                value={typeof selectedCity === 'object' ? selectedCity[language === 'el' ? 'el' : 'en'] : selectedCity}
                onChange={e => {
                  const val = e.target.value;
                  const cityObj = cities.find(c => c.el === val || c.en === val);
                  if (cityObj) {
                    setSelectedCity(cityObj); // Σώζουμε όλο το object {el, en}
                    if (!selectedCities.find(c => c.el === cityObj.el)) {
                      setSelectedCities([cityObj]); // Αντικατάσταση ή προσθήκη στη λίστα
                    }
                  }
                }}
              >
                <option value="">{t.selectCity}</option>
                {cities.map((city, i) => (
                  <option key={i} value={language === 'el' ? city.el : city.en}>
                    {language === 'el' ? city.el : city.en}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex flex-col gap-2">
                {selectedCities.map((cityObj, index) => (
                  <div key={index} className="flex items-center justify-between bg-custom-header rounded-full shadow p-3">
                    <span className="font-medium text-custom-letters">
                      {/* ΠΡΟΣΟΧΗ ΕΔΩ: Επιλέγουμε το πεδίο βάσει γλώσσας */}
                      {language === 'el' ? cityObj.el : cityObj.en}
                    </span>
                    <button onClick={() => removeCity(cityObj)} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-bold px-4 py-2 rounded-full">
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Area */}
            <div className="mt-4 flex flex-col gap-2">
              <label className="block w-full text-center font-semibold mb-2 text-custom-letters">{t.chooseArea}</label>
              <select
                className="w-full p-2 rounded-lg border"
                value={typeof selectedArea === 'object' ? selectedArea[language === 'el' ? 'el' : 'en'] : selectedArea}
                onChange={e => {
                  const val = e.target.value;
                  const areaObj = areas.find(a => a.el === val || a.en === val);
                  if (areaObj) {
                    setSelectedArea(areaObj);
                    if (!selectedAreasList.find(a => a.el === areaObj.el)) {
                      setSelectedAreasList(prev => [...prev, areaObj]); // Εδώ σώζουμε το object {el, en, city_el, city_en}
                    }
                  }
                }}
              >
                <option value="">{t.selectArea}</option>
                {areas.map((area, i) => (
                  <option key={i} value={language === 'el' ? area.el : area.en}>
                    {language === 'el' ? area.el : area.en}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex flex-col gap-2">
                {selectedAreasList.map((areaObj, index) => (
                  <div key={index} className="flex items-center justify-between bg-custom-header rounded-full shadow p-3">
                    <span className="font-medium text-custom-letters">
                      {language === 'el' ? areaObj.el : areaObj.en}
                    </span>
                    <button onClick={() => removeAreaFromList(areaObj)} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-bold px-4 py-2 rounded-full">
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Store */}
            <div className="mt-4 flex flex-col gap-2">
              <label className="block w-full text-center font-semibold mb-2 text-custom-letters">{t.chooseStore}</label>
              <select className="w-full p-2 rounded-lg border" value="" onChange={e => handleSelect("store", e.target.value)}>
                <option value="">{t.selectStore}</option>
                {/*loops through the array to create all the options*/}
                {stores.map(s => (
                  <option key={s.store_id} value={s.store_id}>
                    {s.store_name} ({s.area} - {s.road})
                  </option>
                ))}
              </select>
              <div className="mt-2 flex flex-col gap-2">
                {selected.stores.map((id) => {
                  const store = stores.find((s) => s.store_id === id);
                  return (
                    <div key={id} className="flex items-center justify-between bg-custom-header rounded-full shadow p-3">
                      <span className="font-medium text-custom-letters text-sm text-left">
                        {language === "el"
                          ? (store?.name_el || store?.store_name)
                          : (store?.name_en || store?.store_name)}
                        {store && (
                          <span className="ml-2 opacity-75 italic text-xs">
                            ({store.area}{store.road ? ` - ${store.road}` : ""})
                          </span>
                        )}
                      </span>
                      <button
                        title={t.removeFromPrefs}
                        onClick={() => handleRemove("store", id)} className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-bold px-4 py-2 rounded-full"><Trash2 size={18} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* <div className="flex justify-center mt-4">
              <button onClick={() => navigate("/addstore")} className="inline-flex items-center gap-2 bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-semibold px-4 py-2 rounded-full">
                <Plus size={18} /> Add store
              </button>
            </div> */}

            {/* Map Section */}
            {/* <div className="flex flex-col items-center mt-6">
              <MapContainer center={defaultCenter} zoom={13} style={{ height: "300px", width: "100%", borderRadius: "1rem" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <LocationMarker language={language} t={t} />
                <MapController center={defaultCenter} />
              </MapContainer>

              <div className="mt-4 flex flex-col gap-2 w-full">
                {selectedAreas.map((area) => (
                  <div key={area.uid || area.preference_id} className="flex items-center justify-between bg-custom-header rounded-full shadow p-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-custom-letters text-sm">
                        {language === 'el' ? area.location_el : area.location_en}
                      </span>
                      <span className="text-xs text-custom-letters opacity-70">
                        {area.lat.toFixed(4)}, {area.lng.toFixed(4)}
                      </span>
                    </div>
                    <button
                      title={t.removeFromPrefs}
                      onClick={() => handleRemoveArea(area.uid)}
                      className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-bold px-4 py-2 rounded-full text-xs"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={handleSavePreferences} className="mt-8 bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white font-bold px-6 py-3 rounded-full shadow hover:opacity-90 transition">
                {t.savePrefs}
              </button>
            </div> */}

            <div className="flex justify-center mt-8">
              <button type="button" onClick={handleSavePreferences} className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white font-bold px-6 py-3 rounded-full shadow hover:opacity-90 transition">
                {t.savePrefs}
              </button>
            </div>
          </div>

          {/* Popup */}
          {showSavePopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
              <div role="dialog" aria-modal="true" aria-labelledby="prefs-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                <h3 id="prefs-popup-title" className="text-lg font-semibold text-custom-letters mb-3">{t.prefsSaved}</h3>
                <p className="text-sm text-custom-letters mb-6">{t.prefsSuccessMsg}</p>
                <button onClick={() => setShowSavePopup(false)} className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">{t.ok}</button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CustomizeNotifications;